import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { useAuth } from '../../../auth/AuthProvider';
import { canAccessProviderAdmin } from './providerAdminAccess';

const ORG_TYPES = [
  { value: 'originator', label: 'Originator' },
  { value: 'custodian', label: 'Custodian' },
  { value: 'investor', label: 'Investor (read-only, platform-wide)' },
];
const STATUSES = ['PENDING', 'ACTIVE'];

export function OrganizationCreatePage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    code: '',
    org_type: 'originator',
    contact_email: '',
    status: 'ACTIVE',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!canAccessProviderAdmin(session)) {
    return (
      <p className="text-sm text-eorig-muted">
        Provider Admin access is required to create organizations.
      </p>
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await providerAdminApi.createOrganization({
        name: form.name,
        code: form.code,
        org_type: form.org_type,
        contact_email: form.contact_email || null,
        status: form.status,
      });
      navigate(`/provider/admin/organizations/${created.id}`);
    } catch (err) {
      setError(err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-xl space-y-4 rounded-2xl border border-eorig-border bg-white p-5">
      <p className="text-sm text-eorig-muted">
        Stage 0 — create the institution first. People and Digitize stay locked until this exists.
      </p>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Type</span>
        <select
          required
          value={form.org_type}
          onChange={(e) => setForm((f) => ({ ...f, org_type: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        >
          {ORG_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Name</span>
        <input
          required
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Code</span>
        <input
          required
          value={form.code}
          onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
        {form.org_type === 'originator' && form.code.trim() ? (
          <p className="mt-1.5 text-[12px] text-eorig-muted">
            SFTP / file-share inbox folder on the server:{' '}
            <span className="font-mono">{form.code.trim()}</span> (under the environment mount root).
            DevOps creates this directory after save; share the full path with the client from
            Organization setup.
          </p>
        ) : null}
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Contact email</span>
        <input
          type="email"
          value={form.contact_email}
          onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Status</span>
        <select
          value={form.status}
          onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
          className="w-full rounded-xl border border-eorig-border px-3 py-2"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={saving}
          className="rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Creating…' : 'Create organization'}
        </button>
        <Link to="/provider/admin/organizations" className="rounded-xl px-4 py-2 text-sm text-eorig-muted">
          Cancel
        </Link>
      </div>
    </form>
  );
}
